import { Room } from "../types";

/**
 * Utility functions for room-related operations
 */
export class RoomUtils {
    /**
     * Checks if a room should be treated as a conference room based on its properties
     * Backend now handles exclusions for flex/training rooms and shared desk rooms
     *
     * @param room The room object to check
     * @returns true if the room should be treated as a conference room
     */
    public static isConferenceRoom(room: Room): boolean {
        if (!room?.roomTypes || room.roomTypes.length === 0) {
            return false;
        }

        // Simply check if any room type indicates a conference/meeting room
        // Backend handles exclusions for flex/training rooms and shared desk rooms
        return room.roomTypes.some((roomType) => {
            return this.isConferenceRoomType(roomType.name);
        });
    }

    /**
     * Filters an array of rooms to return only conference rooms
     *
     * @param rooms Array of rooms to filter
     * @returns Array containing only conference rooms
     */
    public static filterConferenceRooms(rooms: Room[]): Room[] {
        return rooms.filter((room) => this.isConferenceRoom(room));
    }

    /**
     * Checks if a room type name indicates a conference/meeting room
     * This is a simplified check for cases where you only have the room type name
     *
     * @param roomTypeName The room type name to check
     * @returns true if the room type indicates a conference/meeting room
     */
    public static isConferenceRoomType(roomTypeName: string): boolean {
        const typeName = roomTypeName?.toLowerCase() || "";
        return (
            typeName.includes("conference") ||
            typeName.includes("meeting") ||
            typeName.includes("besprechung") ||
            typeName.includes("konferenz") ||
            typeName.includes("meeting room") ||
            typeName.includes("conference room")
        );
    }
}
